import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  Contact,
  Download,
  FileDown,
  Grid3x3,
  LoaderCircle,
  MessageSquare,
  RefreshCw,
  Save,
  Settings,
  Ticket,
  Wallet,
  X
} from 'lucide-react';
import { Colonia } from '../../types/colonia';
import { getInscricaoYear } from './adminData';
import { fetchSaldoAsaas, fetchVagasConfig, updateVagasLimite } from './vagasConfigData';
import { baixarDataUrl, gerarCarteirinhaDataUrl, gerarMiniaturasCarteirinha } from './carteirinhaImage';
import { baixarPdfCarteirinhas } from './carteirinhaPdf';
import { baixarDataUrlBingo, gerarCartelaBingoDataUrl, gerarMiniaturaCartelaBingo } from './bingoCartelaImage';
import { baixarPdfCartelasBingo } from './bingoCartelaPdf';
import { CartelaBingo, fetchCartelasBingo } from '../eventoAdicionalData';
import CarteirinhaViewer from './CarteirinhaViewer';

/** Gera em lotes paralelos: baixar 430 fotos uma por vez seria lento demais. */
const LOTE_GERACAO = 6;

type PanelKey = 'limite-inscricoes' | 'carteirinhas' | 'cartelas-bingo' | 'saldo-asaas';

const normalizar = (valor = '') =>
  valor
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const slugNome = (nome: string) =>
  normalizar(nome)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

interface CartaoGerado {
  inscricao: Colonia;
  frente: string;
  verso: string;
}

export const CarteirinhasPanel: React.FC<{ inscricoes: Colonia[] }> = ({ inscricoes }) => {
  const [busca, setBusca] = useState('');
  const [cartoes, setCartoes] = useState<CartaoGerado[]>([]);
  const [progresso, setProgresso] = useState(0);
  const [erro, setErro] = useState('');
  const [visualizando, setVisualizando] = useState<{
    inscricao: Colonia;
    face: 'frente' | 'verso';
    origem: DOMRect;
  } | null>(null);
  const [gerandoPdf, setGerandoPdf] = useState<'todas' | 'selecionadas' | null>(null);
  const [progressoPdf, setProgressoPdf] = useState(0);
  const [totalPdf, setTotalPdf] = useState(0);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());

  const ordenadas = useMemo(
    () => [...inscricoes].sort((a, b) => a.nomeCompleto.localeCompare(b.nomeCompleto, 'pt-BR')),
    [inscricoes]
  );

  useEffect(() => {
    let active = true;
    setCartoes([]);
    setProgresso(0);
    setErro('');
    (async () => {
      for (let inicio = 0; inicio < ordenadas.length; inicio += LOTE_GERACAO) {
        if (!active) return;
        const lote = ordenadas.slice(inicio, inicio + LOTE_GERACAO);
        const resultados = await Promise.all(
          lote.map(async (inscricao) => {
            try {
              const geradas = await gerarMiniaturasCarteirinha(inscricao);
              return geradas ? { inscricao, ...geradas } : null;
            } catch {
              return null;
            }
          })
        );
        if (!active) return;
        const validos = resultados.filter((item): item is CartaoGerado => item !== null);
        if (validos.length < resultados.length) setErro('Algumas carteirinhas não puderam ser geradas.');
        if (validos.length) setCartoes((atual) => [...atual, ...validos]);
        setProgresso((atual) => atual + lote.length);
      }
    })();
    return () => {
      active = false;
    };
  }, [ordenadas]);

  const baixar = async (inscricao: Colonia, face: 'frente' | 'verso') => {
    const dataUrl = await gerarCarteirinhaDataUrl(inscricao, face);
    if (dataUrl) baixarDataUrl(dataUrl, `carteirinha-${slugNome(inscricao.nomeCompleto)}-${face}.png`);
  };

  const baixarEmPdf = async (modo: 'todas' | 'selecionadas', lista: Colonia[]) => {
    if (gerandoPdf || lista.length === 0) return;
    setGerandoPdf(modo);
    setProgressoPdf(0);
    setTotalPdf(lista.length);
    setErro('');
    try {
      await baixarPdfCarteirinhas(lista, (feito) => setProgressoPdf(feito));
    } catch {
      setErro('Não foi possível gerar o PDF.');
    } finally {
      setGerandoPdf(null);
    }
  };

  const alternarSelecao = (id: string) => {
    setSelecionados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const selecionadasList = useMemo(
    () => ordenadas.filter((inscricao) => selecionados.has(inscricao.id || '')),
    [ordenadas, selecionados]
  );

  const filtrados = useMemo(() => {
    const termo = normalizar(busca.trim());
    if (!termo) return cartoes;
    return cartoes.filter((cartao) => normalizar(cartao.inscricao.nomeCompleto).includes(termo));
  }, [busca, cartoes]);

  const carregando = progresso < ordenadas.length;

  return (
    <div className="admin-floating-panel-body">
      <div className="admin-floating-field-row">
        <label className="admin-floating-field">
          <span>Filtrar por nome</span>
          <input
            type="text"
            placeholder="Digite o nome..."
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
          />
        </label>
        {selecionados.size > 0 && (
          <span className="admin-carteirinha-selecao-contador">
            {selecionados.size} selecionada{selecionados.size > 1 ? 's' : ''}
            <button type="button" onClick={() => setSelecionados(new Set())}>
              Limpar
            </button>
          </span>
        )}
      </div>

      <div className="admin-floating-field-row">
        <button
          type="button"
          className="admin-carteirinha-pdf-button"
          onClick={() => void baixarEmPdf('todas', ordenadas)}
          disabled={carregando || gerandoPdf !== null || ordenadas.length === 0}
        >
          {gerandoPdf === 'todas' ? <LoaderCircle size={14} className="admin-spin" /> : <FileDown size={14} />}
          {gerandoPdf === 'todas' ? `Gerando PDF... ${progressoPdf}/${totalPdf}` : `Baixar todas em PDF (${ordenadas.length})`}
        </button>
        <button
          type="button"
          className="admin-carteirinha-pdf-button outline"
          onClick={() => void baixarEmPdf('selecionadas', selecionadasList)}
          disabled={carregando || gerandoPdf !== null || selecionadasList.length === 0}
        >
          {gerandoPdf === 'selecionadas' ? <LoaderCircle size={14} className="admin-spin" /> : <FileDown size={14} />}
          {gerandoPdf === 'selecionadas'
            ? `Gerando PDF... ${progressoPdf}/${totalPdf}`
            : `Baixar selecionadas em PDF (${selecionadasList.length})`}
        </button>
      </div>

      {carregando && (
        <p className="admin-floating-hint">
          <LoaderCircle size={13} className="admin-spin" />
          Gerando carteirinhas... {progresso}/{ordenadas.length}
        </p>
      )}
      {erro && <p className="admin-floating-error">{erro}</p>}
      {!carregando && filtrados.length === 0 && (
        <p className="admin-floating-empty">Nenhuma carteirinha para mostrar.</p>
      )}

      <div className="admin-carteirinha-lista">
        {filtrados.map((cartao) => {
          const id = cartao.inscricao.id || '';
          const selecionado = selecionados.has(id);
          return (
          <div className={`admin-carteirinha-item${selecionado ? ' selecionado' : ''}`} key={cartao.inscricao.id}>
            <div className="admin-carteirinha-item-header">
              <label className="admin-carteirinha-checkbox">
                <input type="checkbox" checked={selecionado} onChange={() => alternarSelecao(id)} />
                <span>{selecionado && <Check size={12} />}</span>
              </label>
              <h4>
                {cartao.inscricao.nomeCompleto}
                <small>{cartao.inscricao.turma || 'Sem turma'}</small>
              </h4>
            </div>
            <div
              className="admin-carteirinha-stack"
              onClick={(event) =>
                setVisualizando({
                  inscricao: cartao.inscricao,
                  face: 'frente',
                  origem: event.currentTarget.getBoundingClientRect()
                })
              }
              role="button"
              tabIndex={0}
            >
              <img
                className="admin-carteirinha-stack-front"
                src={cartao.frente}
                alt={`Carteirinha (frente) de ${cartao.inscricao.nomeCompleto}`}
                loading="lazy"
              />
              <img
                className="admin-carteirinha-stack-back"
                src={cartao.verso}
                alt={`Carteirinha (verso) de ${cartao.inscricao.nomeCompleto}`}
                loading="lazy"
                onClick={(event) => {
                  event.stopPropagation();
                  setVisualizando({
                    inscricao: cartao.inscricao,
                    face: 'verso',
                    origem: event.currentTarget.getBoundingClientRect()
                  });
                }}
              />
              <span className="admin-carteirinha-stack-flip">
                <RefreshCw size={13} />
              </span>
            </div>
            <div className="admin-carteirinha-downloads">
              <button type="button" onClick={() => void baixar(cartao.inscricao, 'frente')}>
                <Download size={12} />
                Frente
              </button>
              <button type="button" onClick={() => void baixar(cartao.inscricao, 'verso')}>
                <Download size={12} />
                Verso
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {visualizando && (
        <CarteirinhaViewer
          inscricao={visualizando.inscricao}
          faceInicial={visualizando.face}
          origem={visualizando.origem}
          onClose={() => setVisualizando(null)}
        />
      )}
    </div>
  );
};

interface CartelaBingoGerada {
  cartela: CartelaBingo;
  imagem: string;
}

type OrdemCartelas = 'recentes' | 'antigas' | 'nome';

export const CartelasBingoPanel: React.FC = () => {
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<OrdemCartelas>('recentes');
  const [cartelasBrutas, setCartelasBrutas] = useState<CartelaBingo[] | null>(null);
  const [erroLista, setErroLista] = useState('');
  const [geradas, setGeradas] = useState<CartelaBingoGerada[]>([]);
  const [progresso, setProgresso] = useState(0);
  const [erro, setErro] = useState('');
  const [gerandoPdf, setGerandoPdf] = useState<'todas' | 'selecionadas' | null>(null);
  const [progressoPdf, setProgressoPdf] = useState(0);
  const [totalPdf, setTotalPdf] = useState(0);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());

  useEffect(() => {
    let ativo = true;
    fetchCartelasBingo()
      .then((lista) => {
        if (ativo) setCartelasBrutas(lista);
      })
      .catch((requestError) => {
        if (ativo) setErroLista(requestError instanceof Error ? requestError.message : 'Não foi possível carregar as cartelas.');
      });
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (!cartelasBrutas) return;
    let active = true;
    setGeradas([]);
    setProgresso(0);
    setErro('');
    (async () => {
      for (let inicio = 0; inicio < cartelasBrutas.length; inicio += LOTE_GERACAO) {
        if (!active) return;
        const lote = cartelasBrutas.slice(inicio, inicio + LOTE_GERACAO);
        const resultados = await Promise.all(
          lote.map(async (cartela) => {
            try {
              const imagem = await gerarMiniaturaCartelaBingo(cartela);
              return imagem ? { cartela, imagem } : null;
            } catch {
              return null;
            }
          })
        );
        if (!active) return;
        const validos = resultados.filter((item): item is CartelaBingoGerada => item !== null);
        if (validos.length < resultados.length) setErro('Algumas cartelas não puderam ser geradas.');
        if (validos.length) setGeradas((atual) => [...atual, ...validos]);
        setProgresso((atual) => atual + lote.length);
      }
    })();
    return () => {
      active = false;
    };
  }, [cartelasBrutas]);

  const baixar = async (cartela: CartelaBingo) => {
    const dataUrl = await gerarCartelaBingoDataUrl(cartela);
    if (dataUrl) baixarDataUrlBingo(dataUrl, `bingo-${slugNome(cartela.nomeCrianca)}-${cartela.id}.png`);
  };

  const baixarEmPdf = async (modo: 'todas' | 'selecionadas', lista: CartelaBingo[]) => {
    if (gerandoPdf || lista.length === 0) return;
    setGerandoPdf(modo);
    setProgressoPdf(0);
    setTotalPdf(lista.length);
    setErro('');
    try {
      await baixarPdfCartelasBingo(lista, (feito) => setProgressoPdf(feito));
    } catch {
      setErro('Não foi possível gerar o PDF.');
    } finally {
      setGerandoPdf(null);
    }
  };

  const alternarSelecao = (id: string) => {
    setSelecionados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const todasCartelas = useMemo(() => geradas.map((item) => item.cartela), [geradas]);
  const selecionadasList = useMemo(
    () => todasCartelas.filter((cartela) => selecionados.has(cartela.id)),
    [todasCartelas, selecionados]
  );

  const filtradas = useMemo(() => {
    const termo = normalizar(busca.trim());
    const lista = termo ? geradas.filter((item) => normalizar(item.cartela.nomeCrianca).includes(termo)) : geradas.slice();
    return lista.sort((a, b) => {
      if (ordem === 'nome') return a.cartela.nomeCrianca.localeCompare(b.cartela.nomeCrianca, 'pt-BR');
      const diferenca = (a.cartela.criadoEmMs || 0) - (b.cartela.criadoEmMs || 0);
      return ordem === 'recentes' ? -diferenca : diferenca;
    });
  }, [busca, geradas, ordem]);

  const carregandoLista = cartelasBrutas === null;
  const carregando = carregandoLista || progresso < (cartelasBrutas?.length || 0);

  return (
    <div className="admin-floating-panel-body">
      <div className="admin-floating-field-row">
        <label className="admin-floating-field">
          <span>Filtrar por nome</span>
          <input
            type="text"
            placeholder="Digite o nome..."
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
          />
        </label>
        <label className="admin-floating-field">
          <span>Ordenar por</span>
          <select value={ordem} onChange={(event) => setOrdem(event.target.value as OrdemCartelas)}>
            <option value="recentes">Mais recentes</option>
            <option value="antigas">Mais antigas</option>
            <option value="nome">Nome</option>
          </select>
        </label>
        {selecionados.size > 0 && (
          <span className="admin-carteirinha-selecao-contador">
            {selecionados.size} selecionada{selecionados.size > 1 ? 's' : ''}
            <button type="button" onClick={() => setSelecionados(new Set())}>
              Limpar
            </button>
          </span>
        )}
      </div>

      <div className="admin-floating-field-row">
        <button
          type="button"
          className="admin-carteirinha-pdf-button"
          onClick={() => void baixarEmPdf('todas', todasCartelas)}
          disabled={carregando || gerandoPdf !== null || todasCartelas.length === 0}
        >
          {gerandoPdf === 'todas' ? <LoaderCircle size={14} className="admin-spin" /> : <FileDown size={14} />}
          {gerandoPdf === 'todas' ? `Gerando PDF... ${progressoPdf}/${totalPdf}` : `Baixar todas em PDF (${todasCartelas.length})`}
        </button>
        <button
          type="button"
          className="admin-carteirinha-pdf-button outline"
          onClick={() => void baixarEmPdf('selecionadas', selecionadasList)}
          disabled={carregando || gerandoPdf !== null || selecionadasList.length === 0}
        >
          {gerandoPdf === 'selecionadas' ? <LoaderCircle size={14} className="admin-spin" /> : <FileDown size={14} />}
          {gerandoPdf === 'selecionadas'
            ? `Gerando PDF... ${progressoPdf}/${totalPdf}`
            : `Baixar selecionadas em PDF (${selecionadasList.length})`}
        </button>
      </div>

      {erroLista && <p className="admin-floating-error">{erroLista}</p>}
      {carregando && !erroLista && (
        <p className="admin-floating-hint">
          <LoaderCircle size={13} className="admin-spin" />
          {carregandoLista ? 'Carregando lista de bingos...' : `Gerando cartelas... ${progresso}/${cartelasBrutas?.length || 0}`}
        </p>
      )}
      {erro && <p className="admin-floating-error">{erro}</p>}
      {!carregando && !erroLista && filtradas.length === 0 && (
        <p className="admin-floating-empty">Nenhuma cartela de bingo para mostrar.</p>
      )}

      <div className="admin-carteirinha-lista">
        {filtradas.map((item) => {
          const { cartela } = item;
          const selecionado = selecionados.has(cartela.id);
          return (
            <div className={`admin-carteirinha-item${selecionado ? ' selecionado' : ''}`} key={cartela.id}>
              <div className="admin-carteirinha-item-header">
                <label className="admin-carteirinha-checkbox">
                  <input type="checkbox" checked={selecionado} onChange={() => alternarSelecao(cartela.id)} />
                  <span>{selecionado && <Check size={12} />}</span>
                </label>
                <h4>
                  {cartela.nomeCrianca}
                  <small>{cartela.turma || 'Sem turma'}</small>
                </h4>
              </div>
              <img className="admin-bingo-thumb" src={item.imagem} alt={`Cartela de bingo de ${cartela.nomeCrianca}`} loading="lazy" />
              <div className="admin-carteirinha-downloads">
                <button type="button" onClick={() => void baixar(cartela)}>
                  <Download size={12} />
                  Baixar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LimiteInscricoesPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmadas, setConfirmadas] = useState<number | null>(null);
  const [limite, setLimite] = useState('');

  useEffect(() => {
    let active = true;
    fetchVagasConfig()
      .then((config) => {
        if (!active) return;
        setLimite(String(config.limite));
        setConfirmadas(config.confirmadas);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar o limite atual.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;

    const parsed = Number(limite);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
      setError('Informe um número inteiro maior que zero.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const config = await updateVagasLimite(parsed);
      setLimite(String(config.limite));
      setConfirmadas(config.confirmadas);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar o limite.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="admin-floating-panel-body" onSubmit={(event) => void handleSubmit(event)}>
      <label className="admin-floating-field">
        <span>Limite de inscrições confirmadas</span>
        <input
          type="number"
          min={1}
          step={1}
          value={limite}
          onChange={(event) => setLimite(event.target.value)}
          disabled={loading || saving}
          required
        />
      </label>
      {confirmadas !== null && (
        <p className="admin-floating-hint">{confirmadas} inscrições confirmadas no período atual.</p>
      )}
      {error && <p className="admin-floating-error">{error}</p>}
      <div className="admin-floating-actions">
        <button type="button" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button type="submit" className="primary" disabled={loading || saving}>
          <Save size={14} />
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
};

const SaldoAsaasPanel: React.FC = () => {
  const [saldo, setSaldo] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const carregar = () => {
    setLoading(true);
    setError('');
    fetchSaldoAsaas()
      .then((valor) => setSaldo(valor))
      .catch((requestError) => {
        setError(requestError instanceof Error ? requestError.message : 'Não foi possível consultar o saldo.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-floating-panel-body">
      {loading && (
        <p className="admin-floating-hint">
          <LoaderCircle size={13} className="admin-spin" />
          Consultando saldo no Asaas...
        </p>
      )}
      {error && <p className="admin-floating-error">{error}</p>}
      {!loading && !error && saldo !== null && (
        <div className="admin-saldo-asaas">
          <span>Saldo disponível no Asaas</span>
          <strong>{saldo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
        </div>
      )}
      <div className="admin-floating-actions">
        <button type="button" className="primary" onClick={carregar} disabled={loading}>
          <RefreshCw size={14} />
          Atualizar
        </button>
      </div>
    </div>
  );
};

const AdminFloatingMenu: React.FC<{ inscricoes?: Colonia[]; selectedYear?: string }> = ({
  inscricoes = [],
  selectedYear
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<PanelKey | null>(null);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "'") {
        event.preventDefault();
        setOpen((current) => !current);
      } else if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setActivePanel(null);
  };

  if (!open) return null;

  return (
    <div className="admin-floating-backdrop" role="presentation" onClick={handleClose}>
      <div
        className={`admin-floating-panel${activePanel === 'carteirinhas' || activePanel === 'cartelas-bingo' ? ' wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de configurações"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-floating-header">
          <h3>
            <Settings size={16} />
            Configurações
          </h3>
          <button type="button" onClick={handleClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        {activePanel === 'limite-inscricoes' ? (
          <LimiteInscricoesPanel onClose={() => setActivePanel(null)} />
        ) : activePanel === 'carteirinhas' ? (
          <CarteirinhasPanel
            inscricoes={selectedYear ? inscricoes.filter((inscricao) => getInscricaoYear(inscricao) === selectedYear) : inscricoes}
          />
        ) : activePanel === 'cartelas-bingo' ? (
          <CartelasBingoPanel />
        ) : activePanel === 'saldo-asaas' ? (
          <SaldoAsaasPanel />
        ) : (
          <ul className="admin-floating-options">
            <li>
              <button type="button" onClick={() => setActivePanel('limite-inscricoes')}>
                <Ticket size={16} />
                Limite de inscrições confirmadas
              </button>
            </li>
            <li>
              <button type="button" onClick={() => setActivePanel('carteirinhas')}>
                <Contact size={16} />
                Carteirinhas
              </button>
            </li>
            <li>
              <button type="button" onClick={() => setActivePanel('cartelas-bingo')}>
                <Grid3x3 size={16} />
                Cartelas Bingo
              </button>
            </li>
            <li>
              <button type="button" onClick={() => setActivePanel('saldo-asaas')}>
                <Wallet size={16} />
                Saldo no Asaas
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  navigate('/controle-mensagens');
                }}
              >
                <MessageSquare size={16} />
                Mensagens
              </button>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminFloatingMenu;
